import { useEffect, useMemo, useRef, useState } from 'react';
import { Renderer2D, PrototypePlayer, safeLink, smartAnimate, pageFlows, videoPlaybackOf, hasVideo, type Document, type MediaAction, type NodeId, type PlayerEvent, type Reaction, type Transition } from '@vellum/core';
import { app } from '../app.ts';
import { MediaPlayer } from '../media.ts';
import { EmbedFrame } from '../canvas/EmbedPlayer.tsx';
import { useApp } from '../hooks.ts';

/** Top-level frames of the current page in reading order (rows, then columns). */
function frames(): NodeId[] {
  const kids = app.doc.children(app.editor.pageId).filter((n) => n.visible && (n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE'));
  return kids.sort((a, b) => (Math.abs(a.y - b.y) > 40 ? a.y - b.y : a.x - b.x)).map((n) => n.id);
}

export function startPresentation() {
  const sel = app.editor.selectedNodes()[0];
  let start: NodeId | undefined;
  if (sel) {
    const top = [sel, ...app.doc.ancestors(sel.id)].find((n) => app.doc.get(n.parent)?.type === 'PAGE');
    if (top && (top.type === 'FRAME' || top.type === 'COMPONENT' || top.type === 'INSTANCE')) start = top.id;
  }
  start ??= pageFlows(app.doc, app.editor.pageId)[0]?.nodeId;
  start ??= frames()[0];
  if (!start || !app.doc.has(start)) return app.toast('Add a frame to present');
  app.setUI({ presentingId: start });
}

interface Shown {
  id: NodeId;
  key: number;
  transition: Transition;
  duration: number;
  back?: boolean;
}

export function Present() {
  const startId = useApp('ui', () => app.ui.presentingId);
  if (!startId) return null;
  return <Player startId={startId} key={startId} />;
}

function Player({ startId }: { startId: NodeId }) {
  // the player runs on a private copy of the document, so presenting never edits the file
  const player = useMemo(() => new PrototypePlayer(app.doc, startId), [startId]);
  const [, setTick] = useState(0);
  const redraw = () => setTick((t) => t + 1);
  // videos and GIFs play while presenting: redraw every frame while something moves
  // video layers play by their own settings and answer the prototype's media actions; their
  // progress and endings fire ON_MEDIA_HIT / ON_MEDIA_END
  const applyRef = useRef<(events: PlayerEvent[]) => void>(() => {});
  const media = useMemo(
    () =>
      new MediaPlayer({
        playback: (id) => videoPlaybackOf(player.doc.get(id)),
        state: (id) => player.media.get(id),
        onTime: (id, from, to) => applyRef.current(player.mediaTime(id, from, to)),
        onEnded: (id) => applyRef.current(player.mediaEnded(id)),
        onFrame: () => redraw(),
      }),
    [player],
  );
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      if (media.moving) redraw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      media.dispose();
    };
  }, [media]);
  const [layers, setLayers] = useState<Shown[]>([{ id: startId, key: 0, transition: 'INSTANT', duration: 0 }]);
  const [hint, setHint] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const keyRef = useRef(1);
  const hover = useRef<{ id: NodeId; revert?: () => PlayerEvent[] } | null>(null);
  const press = useRef<(() => PlayerEvent[]) | null>(null);
  const loaded = useRef(app.docVersion);
  const current = player.current;
  // the page's flows; switching one restarts the presentation there
  const flows = pageFlows(app.doc, app.editor.pageId);
  const flow = flows.find((f) => f.nodeId === startId);

  const [playing, setPlaying] = useState<NodeId | null>(null);
  const [tween, setTween] = useState<{ id: NodeId; doc: Document } | null>(null);
  const apply = (events: PlayerEvent[]) => {
    for (const e of events) {
      if (e.type === 'navigate' && e.transition === 'SMART_ANIMATE') {
        // layers with the same name morph from the old screen into the new one
        hover.current = null;
        const from = layers[layers.length - 1]?.id;
        if (from && player.doc.has(from)) {
          const tw = smartAnimate(player.doc, from, e.id);
          tw.at(0);
          setTween({ id: e.id, doc: tw.doc });
          setLayers([{ id: e.id, key: keyRef.current++, transition: 'INSTANT', duration: 0 }]);
          const t0 = performance.now();
          const dur = Math.max(1, e.duration);
          const step = () => {
            const t = (performance.now() - t0) / dur;
            tw.at(t);
            redraw();
            if (t < 1) requestAnimationFrame(step);
            else setTween(null);
          };
          requestAnimationFrame(step);
          continue;
        }
      }
      if (e.type === 'navigate') {
        hover.current = null;
        setLayers((l) => [...l.slice(-1), { id: e.id, key: keyRef.current++, transition: e.transition === 'SMART_ANIMATE' ? 'DISSOLVE' : e.transition, duration: e.duration, back: e.back }]);
      } else if (e.type === 'url') {
        // link cards with a player play right there; other links open in a new tab
        let card: NodeId | null = null;
        if (player.doc.has(player.screen)) player.doc.walk(player.screen, (n) => void (!card && n.linkPreview?.embed && n.linkPreview.url === e.url && (card = n.id)));
        if (card) setPlaying(card);
        else {
          const link = safeLink(e.url);
          if (link) window.open(link, '_blank', 'noopener');
        }
      }
      else if (e.type === 'media') media.apply(e);
    }
    if (events.length) redraw();
  };
  applyRef.current = apply;

  // follow edits to the real file while presenting (variables and navigation are kept)
  useEffect(() => {
    const off = app.onRender(() => {
      if (loaded.current === app.docVersion) return;
      loaded.current = app.docVersion;
      player.load(app.doc);
      redraw();
    });
    return () => void off();
  }, [player]);

  // AFTER_DELAY reactions on what's shown, rescheduled whenever it changes
  const shownKey = `${player.screen}:${app.docVersion}:${JSON.stringify([...player.vars])}:${player.delayed().map((d) => d.nodeId).join()}`;
  useEffect(() => {
    const timers = player.delayed().map((d) => setTimeout(() => apply(player.fire(d.nodeId, 'AFTER_DELAY').events), d.delay));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const list = frames();
      const i = list.indexOf(player.current);
      if (e.key === 'Escape') app.setUI({ presentingId: null });
      else if (e.key === 'ArrowRight' && i + 1 < list.length) apply(player.run({ type: 'NAVIGATE', destinationId: list[i + 1], transition: 'SLIDE_LEFT', duration: 250 }, list[i]).events);
      else if (e.key === 'ArrowLeft' && i > 0) apply(player.run({ type: 'NAVIGATE', destinationId: list[i - 1], transition: 'SLIDE_RIGHT', duration: 250 }, list[i]).events);
      else if (e.key === 'r') {
        player.vars.clear();
        player.stack = [startId];
        player.overlay = null;
        player.load(app.doc);
        setLayers([{ id: startId, key: keyRef.current++, transition: 'INSTANT', duration: 0 }]);
        redraw();
      } else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  /** Page coordinates of a pointer event over the frame receiving input. */
  const toPage = (clientX: number, clientY: number) => {
    const el = host.current?.querySelector<HTMLCanvasElement>(`[data-frame="${player.screen}"]`);
    if (!el || !player.doc.has(player.screen)) return null;
    const r = el.getBoundingClientRect();
    const b = player.doc.worldBounds(player.screen);
    const s = r.width / b.width;
    return { x: b.x + (clientX - r.left) / s, y: b.y + (clientY - r.top) / s };
  };
  const hitAt = (e: React.PointerEvent | React.MouseEvent, trigger: Reaction['trigger']) => {
    const p = toPage(e.clientX, e.clientY);
    return p ? player.hit(p, trigger) : null;
  };

  return (
    <div
      className="present"
      ref={host}
      onPointerDown={(e) => {
        e.stopPropagation();
        const id = hitAt(e, 'ON_PRESS');
        if (id) {
          const r = player.fire(id, 'ON_PRESS');
          press.current = r.revert ?? null;
          apply(r.events);
        }
      }}
      onPointerUp={() => {
        if (press.current) apply(press.current());
        press.current = null;
      }}
      onClick={(e) => {
        const id = hitAt(e, 'ON_CLICK');
        if (id) apply(player.fire(id, 'ON_CLICK').events);
        else if (player.overlay && !(e.target as HTMLElement).dataset.frame) apply(player.run({ type: 'CLOSE_OVERLAY' }, player.screen).events);
        else if (!hitAt(e, 'ON_PRESS')) {
          setHint(true);
          setTimeout(() => setHint(false), 600);
        }
      }}
      onWheel={(e) => {
        const p = toPage(e.clientX, e.clientY);
        const fid = p && player.scrollableAt(p);
        if (fid && player.scrollBy(fid, e.deltaX, e.deltaY)) redraw();
      }}
      onMouseMove={(e) => {
        const p = toPage(e.clientX, e.clientY);
        const h = hover.current;
        // a hover lasts while the pointer stays over that layer, even if its new variant has no hover reaction
        const still = h && p && player.doc.has(h.id) && (() => {
          const b = player.doc.worldBounds(h.id);
          return p.x >= b.x && p.y >= b.y && p.x <= b.x + b.width && p.y <= b.y + b.height;
        })();
        const hid = still ? h!.id : hitAt(e, 'ON_HOVER');
        if (hover.current?.id !== hid) {
          // leaving restores what the hover changed; entering applies the new one
          if (hover.current?.revert) apply(hover.current.revert());
          hover.current = null;
          if (hid) {
            const r = player.fire(hid, 'ON_HOVER');
            hover.current = { id: hid, revert: r.revert };
            apply(r.events);
          }
        }
        (e.currentTarget as HTMLDivElement).style.cursor = hitAt(e, 'ON_CLICK') || hitAt(e, 'ON_PRESS') ? 'pointer' : 'default';
      }}
    >
      <div className="present-bar">
        {flows.length > 1 ? (
          <select className="share-select" title="Flow" value={flow?.nodeId ?? ''} onChange={(e) => app.setUI({ presentingId: e.target.value })} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            {!flow && <option value="">—</option>}
            {flows.map((f) => (
              <option key={f.nodeId} value={f.nodeId}>
                {f.name}
              </option>
            ))}
          </select>
        ) : (
          flow && <span style={{ opacity: 0.6 }}>{flow.name}</span>
        )}
        <span>{player.doc.get(current)?.name}</span>
        <span style={{ opacity: 0.6 }}>← → frames · R restart · Esc exit</span>
        <button className="btn secondary" onClick={() => app.setUI({ presentingId: null })}>
          Close
        </button>
      </div>
      <div className="present-stage">
        {layers.map((l, i) => (
          <FrameCanvas key={l.key} player={player} media={media} doc={tween?.id === l.id ? tween.doc : undefined} id={l.id} entering={i === layers.length - 1 && layers.length > 1} transition={l.transition} duration={l.duration} back={l.back} />
        ))}
        {player.overlay && (
          <div className="present-overlay">
            <FrameCanvas key={player.overlay} player={player} media={media} id={player.overlay} entering transition="DISSOLVE" duration={200} />
          </div>
        )}
      </div>
      {playing && <PresentEmbed player={player} host={host} id={playing} onClose={() => setPlaying(null)} />}
      <VideoControls player={player} media={media} host={host} run={(id, mediaAction, extra) => apply(player.run({ type: 'UPDATE_MEDIA_RUNTIME', destinationId: id, mediaAction, ...extra }, id).events)} />
      {hint && <div className="present-hint">Click a hotspot (layers with interactions) — or use ← →</div>}
    </div>
  );
}

function FrameCanvas({ player, media, doc: override, id, entering, transition, duration, back }: { player: PrototypePlayer; media: MediaPlayer; doc?: Document; id: NodeId; entering: boolean; transition: Transition; duration: number; back?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  // redraw on every parent render: variant switches and variable changes happen in the player's copy
  useEffect(() => {
    const c = ref.current;
    const doc = override ?? player.doc;
    if (!c || !doc.has(id)) return;
    const b = doc.worldBounds(id);
    const vw = window.innerWidth - 48,
      vh = window.innerHeight - 96;
    const s = Math.min(vw / b.width, vh / b.height, 2);
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(b.width * s * dpr),
      h = Math.round(b.height * s * dpr);
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    if (size.w !== b.width * s || size.h !== b.height * s) setSize({ w: b.width * s, h: b.height * s });
    const ctx = c.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    new Renderer2D(doc).renderSubtree(ctx, id, [s * dpr, 0, 0, s * dpr, -b.x * s * dpr, -b.y * s * dpr], { images: app.images, shaders: app.shaders, media });
  });
  useEffect(() => {
    const onResize = () => setSize({ w: 0, h: 0 });
    window.addEventListener('resize', onResize);
    // images that finish loading need another pass
    const off = app.onRender(() => setSize((x) => ({ ...x })));
    return () => {
      window.removeEventListener('resize', onResize);
      off();
    };
  }, []);
  const anim = entering && transition !== 'INSTANT' ? `present-${transition.toLowerCase().replace('_', '-')}${back ? '-back' : ''} ${duration}ms ease-out both` : undefined;
  return <canvas ref={ref} data-frame={id} className="present-frame" style={{ width: size.w, height: size.h, animation: anim }} />;
}

/** Figma's "Show controls" on video layers: play/pause, sound and a scrubber over each video on screen. */
function VideoControls({ player, media, host, run }: { player: PrototypePlayer; media: MediaPlayer; host: React.RefObject<HTMLDivElement | null>; run: (id: NodeId, a: MediaAction, extra?: { newTimestamp?: number }) => void }) {
  const screen = player.screen;
  const canvas = host.current?.querySelector<HTMLCanvasElement>(`[data-frame="${screen}"]`);
  if (!canvas || !player.doc.has(screen)) return null;
  const ids: NodeId[] = [];
  player.doc.walk(screen, (n) => {
    if (!n.visible) return false;
    if (hasVideo(n) && videoPlaybackOf(n).showControls) ids.push(n.id);
  });
  if (!ids.length) return null;
  const r = canvas.getBoundingClientRect();
  const sb = player.doc.worldBounds(screen);
  const s = r.width / sb.width;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <>
      {ids.map((id) => {
        const b = player.doc.worldBounds(id);
        const st = player.mediaState(id);
        const v = media.videoOf(id);
        const dur = v && Number.isFinite(v.duration) ? v.duration : 0;
        return (
          <div key={id} className="present-video-controls" style={{ left: r.left + (b.x - sb.x) * s, top: r.top + (b.y - sb.y + b.height) * s - 36, width: b.width * s }} onClick={stop} onPointerDown={stop} onPointerUp={stop}>
            <button title={st.playing ? 'Pause' : 'Play'} onClick={() => run(id, 'TOGGLE_PLAY_PAUSE')}>
              {st.playing ? '❚❚' : '▶'}
            </button>
            <input type="range" min={0} max={dur || 1} step={0.05} value={v?.currentTime ?? 0} disabled={!dur} onChange={(e) => run(id, 'SKIP_TO', { newTimestamp: Number(e.target.value) })} />
            <button title={st.muted ? 'Unmute' : 'Mute'} onClick={() => run(id, 'TOGGLE_MUTE_UNMUTE')}>
              {st.muted ? '🔇' : '🔊'}
            </button>
          </div>
        );
      })}
    </>
  );
}

/** A link card's player, over the card on the presented screen. */
function PresentEmbed({ player, host, id, onClose }: { player: PrototypePlayer; host: React.RefObject<HTMLDivElement | null>; id: NodeId; onClose: () => void }) {
  const canvas = host.current?.querySelector<HTMLCanvasElement>(`[data-frame="${player.screen}"]`);
  const n = player.doc.get(id);
  if (!canvas || !n?.linkPreview?.embed || !player.doc.has(player.screen) || !player.doc.isAncestor(player.screen, id)) return null;
  const r = canvas.getBoundingClientRect();
  const sb = player.doc.worldBounds(player.screen);
  const pic = player.doc.children(id).find((c) => c.name === 'Player');
  const b = player.doc.worldBounds(pic?.id ?? id);
  const s = r.width / sb.width;
  const w = b.width * s,
    h = pic ? b.height * s : (w * 9) / 16;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div style={{ position: 'fixed', left: r.left + (b.x - sb.x) * s, top: r.top + (b.y - sb.y) * s, width: w, zIndex: 4 }} onClick={stop} onPointerDown={stop} onPointerUp={stop}>
      <EmbedFrame src={n.linkPreview.embed} title={n.linkPreview.title ?? n.linkPreview.url} style={{ width: w, height: h, display: 'block' }} />
      <button className="btn secondary" style={{ position: 'absolute', top: -30, right: 0 }} onClick={onClose}>
        Close
      </button>
    </div>
  );
}
